/**
 * Pure request-building helpers for `useChat`: payload shape, auth/thread
 * headers, attachment encoding and the non-streaming POST. Nothing in here
 * touches React state; `useChat` owns the send pipeline that calls these.
 */
import type { Attachment, Message, RetryPolicy, SessionConfig } from '../types';
import type { ChatRequest } from '../contracts/chat.types';
import { fetchWithRetry } from '../utils/retryFetch';
import { withRequestTimeout } from '../utils/fetchWithTimeout';
import { messageAnalyticsContext } from '../utils/messageAnalyticsContext';
import { threadCapabilityHeaders } from '../utils/threadCapability';

export interface AttachmentPayload {
  name: string;
  type: string;
  size: number;
  dataUrl?: string;
}

/** Attachment as held by the composer (before encoding for the wire). */
export interface ComposerAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  file?: File;
  previewUrl?: string | null;
}

/** Transcript-safe file labels; never retain file bytes or temporary object URLs. */
export function attachmentMetadata(
  attachments: Pick<Attachment, 'name' | 'size' | 'type'>[],
  messageId: string
): Attachment[] {
  return attachments.map(({ name, size, type }, index) => ({
    id: `${messageId}-attachment-${index}`,
    name,
    size,
    type,
  }));
}

export interface ChatPayloadArgs {
  text: string;
  attachments?: AttachmentPayload[];
  subject?: string;
  tid?: string;
  meta?: Record<string, unknown>;
}

export interface ChatPayloadContext {
  subject: string;
  department: string;
  channel: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  provider?: string;
  threadId: string | null;
  sessionConfig?: SessionConfig;
  /** Current transcript; the last 30 entries ride along as `threadMessages`. */
  messages: Message[];
}

interface RetryMetadata {
  attempt: number;
  reason: 'status' | 'error';
  status?: number;
  error?: Error;
}

/** Shared request body for the streaming and JSON paths. */
export function buildChatPayload(
  { text, attachments: atts = [], subject: subj, tid, meta }: ChatPayloadArgs,
  ctx: ChatPayloadContext
): ChatRequest {
  const analyticsContext = messageAnalyticsContext(ctx.sessionConfig);
  return {
    subject: subj || ctx.subject || (text ? text.slice(0, 80) : 'New message'),
    message: text,
    attachments: (atts || []).map(a => ({
      name: a.name,
      type: a.type,
      size: a.size,
      ...(a.dataUrl ? { dataUrl: a.dataUrl } : {}),
    })),
    meta: {
      ...analyticsContext,
      department: ctx.department,
      customFields: ctx.customFields,
      ...(ctx.metaExtras || {}),
      ...(meta || {}),
    },
    sessionId: analyticsContext.sessionId,
    source: 'response-chat-widget',
    ...(ctx.provider ? { provider: ctx.provider } : {}),
    channel: ctx.channel,
    threadId: tid || ctx.threadId,
    threadMessages: ctx.messages.slice(-30).map(msg => ({
      id: msg.id,
      sender: msg.sender,
      text: msg.text,
      timestamp: msg.timestamp,
      type: msg.type,
      status: msg.status,
      richContent: msg.richContent,
      attachments: msg.attachments?.map(attachment => ({
        name: attachment.name,
        type: attachment.type,
        size: attachment.size,
      })),
    })),
  };
}

/**
 * W1 (docs/contracts/chat-widget.md): the chat POST must carry the per-thread
 * capability like the thread routes already do, or the platform's
 * WIDGET_THREAD_CAP_ENFORCE flip 403s every chat. `sessionConfig` selects the
 * storage the token was written to (`storage: 'session'` -> sessionStorage).
 */
export function buildChatHeaders(
  withAuthHeaders: (headers?: Record<string, string>) => Record<string, string>,
  threadId: string | null | undefined,
  extra?: Record<string, string>,
  sessionConfig?: SessionConfig
): Record<string, string> {
  return {
    ...withAuthHeaders(),
    ...threadCapabilityHeaders(threadId, sessionConfig),
    ...(extra || {}),
  };
}

export function readFileAsDataUrl(file: File, signal?: AbortSignal): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Request aborted', 'AbortError'));
      return;
    }
    const reader = new FileReader();
    const cleanup = () => {
      signal?.removeEventListener('abort', onAbort);
      reader.onload = reader.onerror = reader.onabort = null;
    };
    const onAbort = () => {
      cleanup();
      try {
        reader.abort();
      } catch {
        /* Custom readers may already be closed. */
      }
      reject(signal?.reason ?? new DOMException('Request aborted', 'AbortError'));
    };
    reader.onerror = () => {
      const error = reader.error || new Error('Failed to read file');
      cleanup();
      reject(error);
    };
    reader.onload = () => {
      cleanup();
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onabort = () => {
      cleanup();
      reject(new DOMException('Request aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

/** Encodes composer attachments for the wire; unreadable files fall back to metadata only. */
export async function buildAttachmentPayload(
  attachments: Pick<ComposerAttachment, 'name' | 'type' | 'size' | 'file'>[],
  signal?: AbortSignal
): Promise<AttachmentPayload[]> {
  if (!attachments || attachments.length === 0) return [];
  return Promise.all(
    attachments.map(async att => {
      const base = { name: att.name, type: att.type, size: att.size };
      if (att.file && typeof FileReader !== 'undefined') {
        try {
          const dataUrl = await readFileAsDataUrl(att.file, signal);
          return { ...base, dataUrl };
        } catch (error) {
          if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError'))
            throw error;
          return base;
        }
      }
      return base;
    })
  );
}

/** Server-provided detail from a failed response body (`error`, then `message`). */
export async function readErrorDetail(res: Response): Promise<string> {
  try {
    const json = (await res.json()) as Record<string, unknown>;
    if (typeof json?.error === 'string') return json.error;
    if (typeof json?.message === 'string') return json.message;
  } catch {
    // Keep generic status code message if body is not JSON.
  }
  return '';
}

/** Non-streaming chat POST; resolves to the parsed JSON body or throws with the HTTP status. */
export async function postChatJson({
  endpoint,
  body,
  headers,
  signal,
  retryPolicy,
  onRetry,
}: {
  endpoint: string;
  body: unknown;
  headers: Record<string, string>;
  signal?: AbortSignal;
  retryPolicy?: RetryPolicy;
  onRetry?: (metadata: RetryMetadata) => void;
}): Promise<Record<string, unknown>> {
  return withRequestTimeout(
    async requestSignal => {
      const res = await fetchWithRetry(
        endpoint,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify(body),
          signal: requestSignal,
          timeoutMs: 30_000,
        },
        retryPolicy,
        { onRetry }
      );
      if (!res.ok) {
        const serverError = await readErrorDetail(res);
        throw new Error(
          `Message create failed: ${res.status}${serverError ? ` (${serverError})` : ''}`
        );
      }
      return (await res.json()) as Record<string, unknown>;
    },
    { signal, timeoutMs: 30_000 }
  );
}
